import { useCallback, useEffect, useRef, useState } from "react";

import { MIN_THINKING_MS, runAgent } from "./runner/runAgent";
import type { AgentChoice, AgentContext, AgentIO, AgentMessage, AgentRunControl, AgentStep } from "./runner/types";

export type AgentRunState = "idle" | "running" | "paused" | "finished";

export interface AgentPending {
    choices: AgentChoice<string>[];
}

class AgentCancelledError extends Error {
    constructor() {
        super("Agent run cancelled");
    }
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** Drives `runAgent` from React state; the run starts with `start()` and stops at the next step boundary on `cancel()`. */
export const useAgentRun = (steps: AgentStep[], context: AgentContext) => {
    const [messages, setMessages] = useState<AgentMessage[]>([]);
    const [pending, setPending] = useState<AgentPending | null>(null);
    const [state, setState] = useState<AgentRunState>("idle");
    const [stepNumber, setStepNumber] = useState(0);

    const startedRef = useRef(false);
    const cancelledRef = useRef(false);
    const pausedRef = useRef(false);
    const resumeRef = useRef<(() => void) | null>(null);
    const answerRef = useRef<((value: string) => void) | null>(null);
    const rejectAskRef = useRef<((reason: Error) => void) | null>(null);
    const panelRef = useRef(new Map<string, (result: "done" | "skipped") => void>());
    const counterRef = useRef(0);
    const stepsRef = useRef(steps);
    const contextRef = useRef(context);
    stepsRef.current = steps;
    contextRef.current = context;

    const post = useCallback((m: Omit<AgentMessage, "id">) => {
        const id = `m${++counterRef.current}`;
        setMessages((prev) => [...prev, { ...m, id }]);
        return id;
    }, []);

    const update = useCallback((id: string, patch: Partial<AgentMessage>) => {
        setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));
    }, []);

    const start = useCallback(() => {
        if (startedRef.current) return;
        startedRef.current = true;
        setState("running");

        const io: Omit<AgentIO, "narrateSkipped"> = {
            post,
            update,
            ask: (textId, choices, values) =>
                new Promise((resolve, reject) => {
                    if (cancelledRef.current) {
                        reject(new AgentCancelledError());
                        return;
                    }
                    post({ kind: "question", textId, values });
                    rejectAskRef.current = reject;
                    answerRef.current = (value) => {
                        const choice = choices.find((c) => c.value === value);
                        if (choice) post({ kind: "user", textId: choice.labelId });
                        resolve(value as (typeof choices)[number]["value"]);
                    };
                    setPending({ choices });
                }),
            panel: (panel) =>
                new Promise((resolve) => {
                    if (cancelledRef.current) {
                        resolve("skipped");
                        return;
                    }
                    const id = post({ kind: "question", textId: "app.agent.title", panel });
                    panelRef.current.set(id, (result) => {
                        panelRef.current.delete(id);
                        update(id, { panel: undefined });
                        resolve(result);
                    });
                }),
            think: async (textId) => {
                const id = post({ kind: "thinking", textId });
                await sleep(MIN_THINKING_MS);
                update(id, { kind: "done" });
            },
            isCancelled: () => cancelledRef.current,
        };
        const control: AgentRunControl = {
            isCancelled: () => cancelledRef.current,
            waitIfPaused: () =>
                pausedRef.current ? new Promise<void>((r) => (resumeRef.current = r)) : Promise.resolve(),
        };
        const numbered = stepsRef.current.map((step, index) => ({
            ...step,
            run: async (ctx: AgentContext, stepIo: AgentIO) => {
                setStepNumber(index + 1);
                try {
                    return await step.run(ctx, stepIo);
                } catch (error) {
                    // A question rejected by cancel ends the step quietly; the run loop then stops.
                    if (error instanceof AgentCancelledError) return { status: "failed" as const };
                    throw error;
                }
            },
        }));

        runAgent(numbered, contextRef.current, io, control)
            .catch((error: unknown) => console.error("Agent run failed", error))
            .finally(() => setState("finished"));
    }, [post, update]);

    const answer = useCallback((value: string) => {
        const resolve = answerRef.current;
        answerRef.current = null;
        rejectAskRef.current = null;
        setPending(null);
        resolve?.(value);
    }, []);

    const pause = useCallback(() => {
        pausedRef.current = true;
        setState((s) => (s === "running" ? "paused" : s));
    }, []);

    const resume = useCallback(() => {
        pausedRef.current = false;
        resumeRef.current?.();
        resumeRef.current = null;
        setState((s) => (s === "paused" ? "running" : s));
    }, []);

    const rejectPendingAsk = useCallback(() => {
        const reject = rejectAskRef.current;
        rejectAskRef.current = null;
        answerRef.current = null;
        setPending(null);
        reject?.(new AgentCancelledError());
    }, []);

    const cancel = useCallback(() => {
        cancelledRef.current = true;
        rejectPendingAsk();
        resume();
        panelRef.current.forEach((done) => done("skipped"));
    }, [resume, rejectPendingAsk]);

    const completePanel = useCallback(
        (id: string, result: "done" | "skipped") => panelRef.current.get(id)?.(result),
        [],
    );

    useEffect(
        () => () => {
            cancelledRef.current = true;
            rejectAskRef.current?.(new AgentCancelledError());
        },
        [],
    );

    return {
        messages,
        pending,
        answer,
        pause,
        resume,
        cancel,
        start,
        completePanel,
        state,
        stepNumber,
        stepTotal: steps.length,
    };
};
